import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
    Button,
    Chip,
    CircularProgress,
    DialogActions,
    FormControl,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import MicIcon from '@material-ui/icons/Mic'
import ReplayIcon from '@material-ui/icons/Replay'
import { Container } from '../../components'
import { DropFiles } from '../../components/DropFiles'
import { vendorProjectApi } from '../../contexts'
import { LoriIcon } from '../../navigation/layouts/Lori/Icons/LoriIcon'
import {
    ALL_PRIORITIES,
    ConfirmGeneratedPayload,
    Priority,
    VENDOR_PROJECT_AI_FILE_TYPES,
    VENDOR_PROJECT_AI_MAX_DESCRIPTION_LENGTH,
    VENDOR_PROJECT_AI_MAX_FILE_SIZE,
    VENDOR_PROJECT_AI_MIN_DESCRIPTION_LENGTH,
    VENDOR_PROJECT_AI_SUPPORTED_FILE_EXTENSIONS,
    VendorProjectAiTaskDraft,
    VendorProjectDocument,
    VendorProjectFormData,
    safeDateOnly,
} from '../../models'
import { useUser } from '../../hooks'
import { RootState } from '../../store'
import { GET_USER_LIST_REQUEST } from '../../store/user/types'
import { typographyStyle } from '../../theme/typographyStyle'
import { ProjectFieldsEditor } from './ProjectFieldsEditor'
import {
    ProjectFieldErrors,
    todayYYYYMMDD,
    validateProjectFields,
} from './projectFormHelpers'
import { fromDateInput, toDateInput } from './taskDateHelpers'

type Stage = 'input' | 'loading' | 'review'

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike

interface SpeechRecognitionResultLike {
    isFinal: boolean
    0: {
        transcript: string
    }
}

interface SpeechRecognitionEventLike {
    resultIndex: number
    results: {
        length: number
        [index: number]: SpeechRecognitionResultLike
    }
}

interface SpeechRecognitionLike {
    continuous: boolean
    interimResults: boolean
    lang: string
    onresult: ((event: SpeechRecognitionEventLike) => void) | null
    onerror: (() => void) | null
    onend: (() => void) | null
    start: () => void
    stop: () => void
}

const useVoiceStyles = makeStyles({
    micButton: {
        position: 'relative',
        zIndex: 1,
    },
    micPulse: {
        position: 'absolute',
        top: -8,
        left: -8,
        width: 48,
        height: 48,
        borderRadius: '50%',
        background: 'rgba(244,67,54,0.18)',
        boxShadow: '0 0 0 0 rgba(244,67,54,0.55)',
        animation: '$pulse 1.2s infinite',
        zIndex: 0,
    },
    '@keyframes pulse': {
        '0%': {
            transform: 'scale(1)',
            opacity: 1,
        },
        '70%': {
            transform: 'scale(1.5)',
            opacity: 0.3,
        },
        '100%': {
            transform: 'scale(2)',
            opacity: 0,
        },
    },
})

interface TaskDraftForm {
    title: string
    description: string
    schedule_time: string
    address: string
}

interface Props {
    onCreated: () => void
    onDirtyChange?: (dirty: boolean) => void
}

const addDays = (dateStr: string, days: number): string => {
    const d = new Date(dateStr)
    d.setDate(d.getDate() + days)
    const pad = (n: number) => `${n}`.padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const normalizePriority = (priority: unknown): Priority => {
    return ALL_PRIORITIES.includes(priority as Priority)
        ? (priority as Priority)
        : 'MEDIUM'
}

const draftProjectToForm = (
    document: VendorProjectDocument,
): VendorProjectFormData => {
    const draft = document.ai_draft?.project
    const today = todayYYYYMMDD()
    return {
        title: draft?.title ?? '',
        description: draft?.description ?? '',
        start_date: safeDateOnly(draft?.start_date) ?? today,
        end_date: safeDateOnly(draft?.end_date) ?? addDays(today, 14),
        priority: normalizePriority(draft?.priority),
        address: draft?.address ?? '',
    }
}

const draftTasksToForm = (
    tasks: VendorProjectAiTaskDraft[] | undefined,
    fallbackAddress: string,
): TaskDraftForm[] => {
    return (tasks ?? []).map((task) => ({
        title: task.title ?? '',
        description: task.description ?? '',
        schedule_time: toDateInput(task.schedule_time),
        address: task.address ?? fallbackAddress ?? '',
    }))
}

const emptyTask = (address: string): TaskDraftForm => ({
    title: '',
    description: '',
    schedule_time: '',
    address,
})

const ErrorBanner = ({
    title,
    message,
}: {
    title: string
    message: string
}) => {
    const theme = useTheme()
    return (
        <Container
            alignItems="flex-start"
            style={{
                gap: theme.spacing(1),
                padding: theme.spacing(1.25, 1.5),
                borderRadius: 8,
                border: `1px solid ${theme.palette.error.light}`,
                backgroundColor: '#ffebee',
                color: theme.palette.error.dark,
            }}
        >
            <ErrorOutlineIcon fontSize="small" style={{ marginTop: 2 }} />
            <Container
                direction="column"
                flex={1}
                style={{ ...typographyStyle(theme.typography.body2) }}
            >
                <span style={{ fontWeight: 700 }}>{title}</span>
                <span>{message}</span>
            </Container>
        </Container>
    )
}

export const AiProjectGenerator = ({ onCreated, onDirtyChange }: Props) => {
    const theme = useTheme()
    const voiceClasses = useVoiceStyles()
    const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
    const { workspaceUser } = useUser()
    const userList = useSelector((s: RootState) => s.user.userList)
    const usersLoading = useSelector(
        (s: RootState) => s.user.isLoading[GET_USER_LIST_REQUEST],
    )
    const { generateProjectDraft, confirmGeneratedProject } = vendorProjectApi()

    const [stage, setStage] = useState<Stage>('input')
    const [file, setFile] = useState<File | null>(null)
    const [description, setDescription] = useState('')
    const [document, setDocument] = useState<VendorProjectDocument | null>(null)
    const [projectForm, setProjectForm] = useState<VendorProjectFormData>({
        title: '',
        description: '',
        start_date: todayYYYYMMDD(),
        end_date: addDays(todayYYYYMMDD(), 14),
        priority: 'MEDIUM',
        address: '',
    })
    const [tasks, setTasks] = useState<TaskDraftForm[]>([])
    const [assignedToId, setAssignedToId] = useState<number | ''>(
        workspaceUser?.id ?? '',
    )
    const [projectErrors, setProjectErrors] = useState<ProjectFieldErrors>({})
    const [taskErrors, setTaskErrors] = useState<Record<number, string>>({})
    const [error, setError] = useState<string | null>(null)
    const [recordingDescription, setRecordingDescription] = useState(false)
    const [voiceError, setVoiceError] = useState<string | null>(null)
    const [interimTranscript, setInterimTranscript] = useState('')

    const trimmedDescription = description.trim()
    const descriptionLength = trimmedDescription.length
    const descriptionError =
        descriptionLength > 0
            ? descriptionLength < VENDOR_PROJECT_AI_MIN_DESCRIPTION_LENGTH
                ? `Description must be at least ${VENDOR_PROJECT_AI_MIN_DESCRIPTION_LENGTH} characters.`
                : descriptionLength > VENDOR_PROJECT_AI_MAX_DESCRIPTION_LENGTH
                ? `Description must be ${VENDOR_PROJECT_AI_MAX_DESCRIPTION_LENGTH} characters or fewer.`
                : ''
            : ''

    useEffect(() => {
        onDirtyChange?.(
            stage !== 'input' ||
                !!file ||
                description.trim().length > 0 ||
                tasks.length > 0,
        )
    }, [description, file, onDirtyChange, stage, tasks.length])

    const canGenerate = useMemo(() => {
        const hasValidDescription =
            descriptionLength >= VENDOR_PROJECT_AI_MIN_DESCRIPTION_LENGTH &&
            descriptionLength <= VENDOR_PROJECT_AI_MAX_DESCRIPTION_LENGTH
        return (!!file || hasValidDescription) && !descriptionError
    }, [descriptionError, descriptionLength, file])

    const assigneeOptions = useMemo(() => {
        if (!workspaceUser) return userList
        const hasCurrentUser = userList.some(
            (user) => user.id === workspaceUser.id,
        )
        return hasCurrentUser ? userList : [workspaceUser, ...userList]
    }, [userList, workspaceUser])

    useEffect(() => {
        if (assignedToId !== '') return
        const fallbackId = workspaceUser?.id ?? assigneeOptions[0]?.id
        if (fallbackId) setAssignedToId(fallbackId)
    }, [assignedToId, assigneeOptions, workspaceUser?.id])

    useEffect(() => {
        return () => {
            recognitionRef.current?.stop()
            recognitionRef.current = null
        }
    }, [])

    const getSpeechRecognition = (): SpeechRecognitionConstructor | null => {
        const win = window as typeof window & {
            SpeechRecognition?: SpeechRecognitionConstructor
            webkitSpeechRecognition?: SpeechRecognitionConstructor
        }
        return win.SpeechRecognition ?? win.webkitSpeechRecognition ?? null
    }

    const appendTranscript = (transcript: string) => {
        const cleaned = transcript.trim()
        if (!cleaned) return
        setDescription((prev) => {
            const separator = prev.trim().length > 0 ? ' ' : ''
            return `${prev}${separator}${cleaned}`
        })
    }

    const requestMicrophonePermission = async (): Promise<boolean> => {
        if (!navigator.mediaDevices?.getUserMedia) {
            setVoiceError('Microphone access is not supported in this browser.')
            return false
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            })
            stream.getTracks().forEach((track) => track.stop())
            return true
        } catch {
            setVoiceError(
                'Microphone permission is required to dictate the description.',
            )
            return false
        }
    }

    const startDescriptionRecording = async () => {
        const SpeechRecognition = getSpeechRecognition()
        if (!SpeechRecognition) {
            setVoiceError('Voice input is not supported in this browser.')
            return
        }

        setVoiceError(null)
        const hasPermission = await requestMicrophonePermission()
        if (!hasPermission) return

        const recognition = new SpeechRecognition()
        recognition.continuous = true
        recognition.interimResults = true
        recognition.lang = 'en-US'
        recognition.onresult = (event) => {
            let finalText = ''
            let interimText = ''
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i]
                const transcript = result[0]?.transcript ?? ''
                if (result.isFinal) {
                    finalText += transcript
                } else {
                    interimText += transcript
                }
            }
            appendTranscript(finalText)
            setInterimTranscript(interimText.trim())
        }
        recognition.onerror = () => {
            setVoiceError(
                'Could not capture audio. Check microphone permissions.',
            )
            setRecordingDescription(false)
            setInterimTranscript('')
        }
        recognition.onend = () => {
            setRecordingDescription(false)
            setInterimTranscript('')
            recognitionRef.current = null
        }

        setInterimTranscript('')
        recognitionRef.current = recognition
        try {
            recognition.start()
            setRecordingDescription(true)
        } catch {
            recognitionRef.current = null
            setVoiceError('Could not start voice input. Please try again.')
        }
    }

    const stopDescriptionRecording = () => {
        recognitionRef.current?.stop()
        setRecordingDescription(false)
        setInterimTranscript('')
    }

    const selectFile = (nextFile: File | null) => {
        setError(null)
        if (!nextFile) {
            setFile(null)
            return
        }
        const lowerName = nextFile.name.toLowerCase()
        const supported = VENDOR_PROJECT_AI_SUPPORTED_FILE_EXTENSIONS.some(
            (ext) => lowerName.endsWith(ext),
        )
        if (!supported) {
            setError(
                `Unsupported file type. Allowed file types: ${VENDOR_PROJECT_AI_SUPPORTED_FILE_EXTENSIONS.join(
                    ', ',
                )}`,
            )
            return
        }
        if (nextFile.size > VENDOR_PROJECT_AI_MAX_FILE_SIZE) {
            setError('File must be 10MB or smaller.')
            return
        }
        setFile(nextFile)
    }

    const handleGenerate = async () => {
        if (!canGenerate) {
            setError(
                descriptionError ||
                    'Upload a document, add a valid description, or provide both.',
            )
            return
        }
        setError(null)
        setStage('loading')
        try {
            const generated = await generateProjectDraft({
                file,
                description: trimmedDescription || null,
            })
            const nextProject = draftProjectToForm(generated)
            const nextTasks = draftTasksToForm(
                generated.ai_draft?.tasks,
                nextProject.address ?? '',
            )
            setDocument(generated)
            setProjectForm(nextProject)
            setTasks(
                nextTasks.length > 0
                    ? nextTasks
                    : [emptyTask(nextProject.address ?? '')],
            )
            setAssignedToId(workspaceUser?.id ?? assigneeOptions[0]?.id ?? '')
            setProjectErrors({})
            setTaskErrors({})
            setStage('review')
        } catch (e) {
            setError(
                e instanceof Error && e.message
                    ? e.message
                    : 'Failed to generate project draft',
            )
            setStage('input')
        }
    }

    const validateTasks = (): boolean => {
        const next: Record<number, string> = {}
        tasks.forEach((task, idx) => {
            if (!task.title.trim()) next[idx] = 'Task title is required'
        })
        if (tasks.length === 0) {
            next[0] = 'At least one task is required'
        }
        setTaskErrors(next)
        return Object.keys(next).length === 0
    }

    const handleConfirm = async () => {
        const nextProjectErrors = validateProjectFields(projectForm)
        setProjectErrors(nextProjectErrors)
        const tasksValid = validateTasks()
        if (Object.keys(nextProjectErrors).length > 0 || !tasksValid) {
            return
        }

        setError(null)
        setStage('loading')
        try {
            const payload: ConfirmGeneratedPayload = {
                document_id: document?.id,
                assigned_to_id:
                    assignedToId === '' ? undefined : Number(assignedToId),
                project: {
                    title: projectForm.title,
                    description: projectForm.description ?? '',
                    start_date: projectForm.start_date,
                    end_date: projectForm.end_date,
                    priority: projectForm.priority,
                    address: projectForm.address ?? '',
                },
                tasks: tasks.map((task) => ({
                    title: task.title,
                    description: task.description,
                    schedule_time: fromDateInput(task.schedule_time) ?? null,
                    address: task.address,
                })),
            }
            await confirmGeneratedProject(payload)
            onCreated()
        } catch (e) {
            setError(
                e instanceof Error && e.message
                    ? e.message
                    : 'Failed to confirm generated project',
            )
            setStage('review')
        }
    }

    const reset = () => {
        setStage('input')
        setFile(null)
        setDescription('')
        setDocument(null)
        setTasks([])
        setProjectErrors({})
        setTaskErrors({})
        setError(null)
    }

    if (stage === 'loading') {
        return (
            <Paper
                elevation={0}
                style={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 14,
                    overflow: 'hidden',
                    backgroundColor: 'white',
                }}
            >
                <LinearProgress />
                <Container
                    direction="column"
                    alignItems="center"
                    style={{ padding: theme.spacing(6, 4) }}
                >
                    <Container
                        alignItems="center"
                        justifyContent="center"
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            backgroundColor: '#e8efef',
                            color: theme.palette.brand1.main,
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        <LoriIcon
                            width={34}
                            height={34}
                            color={theme.palette.brand1.main}
                        />
                    </Container>
                    <CircularProgress size={34} />
                    <Container
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            marginTop: theme.spacing(2.5),
                            fontWeight: 700,
                        }}
                    >
                        Lori is reading your document and drafting tasks...
                    </Container>
                    <Container
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            color: theme.palette.text.secondary,
                            marginTop: theme.spacing(0.5),
                        }}
                    >
                        This can take a moment.
                    </Container>
                </Container>
            </Paper>
        )
    }

    if (stage === 'review') {
        return (
            <Container direction="column" style={{ gap: theme.spacing(2) }}>
                {error && (
                    <ErrorBanner
                        title="Could not confirm generated project"
                        message={error}
                    />
                )}
                <Paper
                    elevation={0}
                    style={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 14,
                        overflow: 'hidden',
                        backgroundColor: 'white',
                    }}
                >
                    <Container
                        direction="column"
                        style={{
                            padding: theme.spacing(2, 2, 1.5),
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            backgroundColor: '#fbfcfc',
                        }}
                    >
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.subtitle1),
                                fontWeight: 700,
                            }}
                        >
                            Review Project Details
                        </Container>
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.secondary,
                                marginTop: 2,
                            }}
                        >
                            Make any edits before creating the actual project.
                        </Container>
                    </Container>
                    <Container
                        direction="column"
                        style={{
                            gap: theme.spacing(2),
                            padding: theme.spacing(2),
                        }}
                    >
                        <ProjectFieldsEditor
                            value={projectForm}
                            onChange={setProjectForm}
                            errors={projectErrors}
                        />
                    </Container>
                </Paper>

                <Paper
                    elevation={0}
                    style={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 14,
                        overflow: 'hidden',
                        backgroundColor: 'white',
                    }}
                >
                    <Container
                        alignItems="center"
                        style={{
                            padding: theme.spacing(2, 2, 1.5),
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            backgroundColor: '#fbfcfc',
                            flexWrap: 'wrap',
                            gap: theme.spacing(1),
                        }}
                    >
                        <Container direction="column">
                            <Container
                                style={{
                                    ...typographyStyle(
                                        theme.typography.subtitle1,
                                    ),
                                    fontWeight: 700,
                                }}
                            >
                                Generated Tasks
                            </Container>
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    color: theme.palette.text.secondary,
                                    marginTop: 2,
                                }}
                            >
                                Review, add or remove tasks before confirming.
                            </Container>
                        </Container>
                        <Container flex={1} />
                        <FormControl
                            size="small"
                            variant="outlined"
                            style={{
                                minWidth: 220,
                                marginRight: theme.spacing(1),
                            }}
                        >
                            <InputLabel id="ai-task-assignee-label">
                                Assign all tasks to
                            </InputLabel>
                            <Select
                                labelId="ai-task-assignee-label"
                                label="Assign all tasks to"
                                value={assignedToId}
                                onChange={(e) =>
                                    setAssignedToId(Number(e.target.value))
                                }
                            >
                                {usersLoading && assigneeOptions.length === 0 && (
                                    <MenuItem disabled value="">
                                        Loading users...
                                    </MenuItem>
                                )}
                                {!usersLoading && assigneeOptions.length === 0 && (
                                    <MenuItem disabled value="">
                                        No users available
                                    </MenuItem>
                                )}
                                {assigneeOptions.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.id === workspaceUser?.id
                                            ? `${user.name} (you)`
                                            : user.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() =>
                                setTasks([
                                    ...tasks,
                                    emptyTask(projectForm.address ?? ''),
                                ])
                            }
                        >
                            Add Task
                        </Button>
                    </Container>

                    <Container
                        direction="column"
                        style={{
                            gap: theme.spacing(1.5),
                            padding: theme.spacing(2),
                        }}
                    >
                        {tasks.map((task, idx) => (
                            <Paper
                                key={`ai-task-${idx}`}
                                elevation={0}
                                style={{
                                    border: `1px solid ${
                                        taskErrors[idx]
                                            ? theme.palette.error.light
                                            : theme.palette.divider
                                    }`,
                                    borderRadius: 10,
                                    padding: theme.spacing(1.5),
                                    backgroundColor: '#ffffff',
                                }}
                            >
                                <Container
                                    direction="column"
                                    style={{ gap: theme.spacing(1.5) }}
                                >
                                    <Container alignItems="center">
                                        <Container
                                            alignItems="center"
                                            justifyContent="center"
                                            style={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: '50%',
                                                backgroundColor: '#e8efef',
                                                color:
                                                    theme.palette.brand1.main,
                                                fontWeight: 800,
                                                flexShrink: 0,
                                                marginRight: theme.spacing(1),
                                            }}
                                        >
                                            {idx + 1}
                                        </Container>
                                        <TextField
                                            label="Task Title"
                                            value={task.title}
                                            onChange={(e) => {
                                                const next = [...tasks]
                                                next[idx] = {
                                                    ...task,
                                                    title: e.target.value,
                                                }
                                                setTasks(next)
                                            }}
                                            required
                                            error={!!taskErrors[idx]}
                                            helperText={taskErrors[idx]}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                        />
                                        <Tooltip title="Remove task">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={tasks.length <= 1}
                                                    onClick={() =>
                                                        setTasks(
                                                            tasks.filter(
                                                                (_, i) =>
                                                                    i !== idx,
                                                            ),
                                                        )
                                                    }
                                                    style={{
                                                        marginLeft: theme.spacing(
                                                            1,
                                                        ),
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Container>
                                    <Container
                                        style={{ gap: theme.spacing(1.5) }}
                                    >
                                        <TextField
                                            label="Scheduled Date"
                                            type="date"
                                            value={task.schedule_time}
                                            onChange={(e) => {
                                                const next = [...tasks]
                                                next[idx] = {
                                                    ...task,
                                                    schedule_time:
                                                        e.target.value,
                                                }
                                                setTasks(next)
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                        />
                                        <TextField
                                            label="Address"
                                            value={task.address}
                                            onChange={(e) => {
                                                const next = [...tasks]
                                                next[idx] = {
                                                    ...task,
                                                    address: e.target.value,
                                                }
                                                setTasks(next)
                                            }}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                        />
                                    </Container>
                                    <TextField
                                        label="Description"
                                        value={task.description}
                                        onChange={(e) => {
                                            const next = [...tasks]
                                            next[idx] = {
                                                ...task,
                                                description: e.target.value,
                                            }
                                            setTasks(next)
                                        }}
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        fullWidth
                                    />
                                </Container>
                            </Paper>
                        ))}
                    </Container>
                </Paper>

                <DialogActions
                    style={{
                        padding: theme.spacing(1.5, 0, 0),
                    }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<ReplayIcon />}
                        onClick={reset}
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Start Over
                    </Button>
                    <Container flex={1} />
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={
                            <LoriIcon width={18} height={18} color="white" />
                        }
                        onClick={handleConfirm}
                        style={{ textTransform: 'none', fontWeight: 700 }}
                    >
                        Confirm Project
                    </Button>
                </DialogActions>
            </Container>
        )
    }

    return (
        <Container direction="column" style={{ gap: theme.spacing(2) }}>
            {error && (
                <ErrorBanner
                    title="Could not generate project draft"
                    message={error}
                />
            )}
            <Paper
                elevation={0}
                style={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 14,
                    overflow: 'hidden',
                    backgroundColor: 'white',
                }}
            >
                <Container
                    alignItems="center"
                    style={{
                        padding: theme.spacing(2),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: '#fbfcfc',
                        flexWrap: 'wrap',
                        gap: theme.spacing(1),
                    }}
                >
                    <Container direction="column" flex={1}>
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.subtitle1),
                                fontWeight: 700,
                            }}
                        >
                            Start with source material
                        </Container>
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.secondary,
                                marginTop: 2,
                            }}
                        >
                            Paste a description or upload a scope document. Lori
                            returns an editable draft before anything is saved.
                        </Container>
                    </Container>
                </Container>

                <Container
                    direction="column"
                    style={{
                        padding: theme.spacing(2),
                        gap: theme.spacing(2),
                    }}
                >
                    <Container
                        direction="column"
                        style={{ gap: theme.spacing(1) }}
                    >
                        <TextField
                            label="Project Description or Instructions"
                            placeholder="Describe the project scope, timeline, location, tasks, or instructions for Lori. You can also upload a document below."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            multiline
                            rows={6}
                            variant="outlined"
                            fullWidth
                            error={!!descriptionError}
                            helperText={
                                descriptionError ||
                                `${descriptionLength.toLocaleString()} / ${VENDOR_PROJECT_AI_MAX_DESCRIPTION_LENGTH.toLocaleString()} characters (minimum ${VENDOR_PROJECT_AI_MIN_DESCRIPTION_LENGTH} when provided)`
                            }
                        />
                        <Container
                            alignItems="center"
                            style={{
                                gap: theme.spacing(1),
                                flexWrap: 'wrap',
                            }}
                        >
                            <Tooltip
                                title={
                                    recordingDescription
                                        ? 'Stop recording'
                                        : 'Dictate description'
                                }
                            >
                                <span
                                    style={{
                                        position: 'relative',
                                        display: 'inline-block',
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        className={voiceClasses.micButton}
                                        onClick={
                                            recordingDescription
                                                ? stopDescriptionRecording
                                                : startDescriptionRecording
                                        }
                                        aria-label={
                                            recordingDescription
                                                ? 'Stop recording description'
                                                : 'Start recording description'
                                        }
                                        style={{
                                            backgroundColor: recordingDescription
                                                ? '#ffebee'
                                                : '#eef5f3',
                                            border: recordingDescription
                                                ? '2px solid #f44336'
                                                : `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        {recordingDescription && (
                                            <span
                                                className={
                                                    voiceClasses.micPulse
                                                }
                                            />
                                        )}
                                        <MicIcon
                                            fontSize="small"
                                            htmlColor={
                                                recordingDescription
                                                    ? '#f44336'
                                                    : theme.palette.brand1.main
                                            }
                                        />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Container
                                direction="column"
                                style={{
                                    ...typographyStyle(
                                        theme.typography.caption,
                                    ),
                                    color: voiceError
                                        ? theme.palette.error.main
                                        : theme.palette.text.secondary,
                                }}
                            >
                                <span>
                                    {voiceError ??
                                        (recordingDescription
                                            ? 'Listening... click the mic again to stop.'
                                            : 'Click the mic to dictate into the description.')}
                                </span>
                                {interimTranscript && (
                                    <span>{`"${interimTranscript}"`}</span>
                                )}
                            </Container>
                        </Container>
                    </Container>
                    <Paper
                        elevation={0}
                        style={{
                            border: `1px dashed ${theme.palette.brand1.main}`,
                            borderRadius: 12,
                            padding: theme.spacing(2),
                            backgroundColor: '#f7fbfa',
                        }}
                    >
                        <Container
                            direction="column"
                            alignItems="center"
                            style={{
                                textAlign: 'center',
                                gap: theme.spacing(1.5),
                            }}
                        >
                            <DropFiles
                                multipleFiles={false}
                                fileTypes={VENDOR_PROJECT_AI_FILE_TYPES}
                                displayString="Drag and drop a scope document here, or click to choose a file"
                                onDrop={(files) => selectFile(files[0] ?? null)}
                            />
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                PDF, DOCX, TXT, MD, HTML, or HTM. Max 10MB.
                                Uploaded documents are used as the main source;
                                your description adds context or instructions.
                            </Container>
                            {file && (
                                <Chip
                                    label={file.name}
                                    onDelete={() => setFile(null)}
                                />
                            )}
                        </Container>
                    </Paper>
                </Container>
            </Paper>

            <Container
                alignItems="center"
                style={{
                    padding: theme.spacing(1.5, 2),
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 12,
                    backgroundColor: 'white',
                    gap: theme.spacing(1),
                    flexWrap: 'wrap',
                }}
            >
                <Container
                    style={{
                        ...typographyStyle(theme.typography.body2),
                        color: theme.palette.text.secondary,
                    }}
                >
                    Lori will create an editable draft first. Nothing is saved
                    until you confirm the review screen.
                </Container>
                <Container flex={1} />
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={
                        <LoriIcon width={18} height={18} color="white" />
                    }
                    disabled={!canGenerate}
                    onClick={handleGenerate}
                    style={{ textTransform: 'none', fontWeight: 700 }}
                >
                    Generate with Lori
                </Button>
            </Container>
        </Container>
    )
}
